import { waitForPaidSession } from "@/utils/signup";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  sendWelcomeEmail,
  updateOnboardingStatus,
  useStripePricesQuery,
} from "@/api/vercel";
import { useCustomer } from "@/hooks/useCustomer";
import { clearAuthStorage, getOnboardingStatus, storeAuthData } from "@/utils/auth";
import {
  trackCompleteRegistration,
  trackMetaPurchase,
} from "@/utils/facebook-pixel-events";
import logger from "@/utils/logger";
import {
  decodeJwtPayload,
  getStoredProductName,
  persistProductName,
} from "@/utils/product";
import { PRODUCT_LEXI_PREMIUM } from "@/utils/vars";
import { landingAfterSignIn } from "@/utils/todays-tips-ready";
import { Check, Loader2, LogOut } from "lucide-react";
import OnboardingCheckout from "@/components/checkout/OnboardingCheckout";
import { Button } from "@/components/ui/button";

const formatPrice = (amount, currency = "AUD") => {
  if (amount === undefined || amount === null) return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return null;
  // Stripe amounts are in cents (smallest currency unit)
  // If amount is >= 100, assume it's in cents and convert to dollars
  // If amount is < 100, assume it's already in dollars
  const normalized = num >= 100 ? num / 100 : num;
  return normalized.toLocaleString("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeCurrencyAmount = (amount) => {
  if (amount === undefined || amount === null) return 0;
  const num = Number(amount);
  if (Number.isNaN(num)) return 0;
  // Stripe amounts are in cents. If the number is >= 100, we treat it as cents.
  // This heuristic works for Lexi's price points ($9.95, $29.95).
  const normalized = num >= 100 ? num / 100 : num;
  return normalized;
};

const getMetaPurchaseData = (
  paymentIntent,
  selectedPlan,
  lexiProPlan,
  dayPassPlan,
) => {
  const plan = selectedPlan === "day-pass" ? dayPassPlan : lexiProPlan;
  // If we don't have a plan even after choosing, we can't track accurately
  if (!plan && !paymentIntent) return null;

  const amount = paymentIntent?.amount ?? plan?.amount ?? 0;
  const currency = (
    paymentIntent?.currency ||
    plan?.currency ||
    "AUD"
  ).toUpperCase();

  const value = normalizeCurrencyAmount(amount);

  // Meta requires a valid non-zero value and 3-letter currency
  if (value <= 0 || !currency || currency.length !== 3) {
    logger.warn("Invalid purchase data for Meta tracking:", {
      value,
      currency,
    });
    return null;
  }

  return {
    value,
    currency,
    productName: plan?.productName || plan?.description || "Lexi Pro",
    eventId: paymentIntent?.id,
  };
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId, stripeCustomerId } = useCustomer();
  const [handoffError, setHandoffError] = useState("");
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isAutoSelectingPro, setIsAutoSelectingPro] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading your plan...");

  const userEmail =
    sessionStorage.getItem("userEmail") ||
    localStorage.getItem("userEmail") ||
    "";
  const userPhone =
    sessionStorage.getItem("phoneNumber") ||
    localStorage.getItem("phoneNumber") ||
    "";

  const { data: stripePricesData, isLoading: isPriceLoading } =
    useStripePricesQuery({
      refetchOnMount: true,
    });

  // Filter to get Lexi Pro product with monthly recurring price
  const lexiProProduct = stripePricesData?.products?.find(
    (product) => product?.name === PRODUCT_LEXI_PREMIUM,
  );

  const lexiProPrice = lexiProProduct?.prices?.find(
    (price) => price.recurring?.interval === "month",
  );

  const lexiProPlan = lexiProPrice
    ? {
        productId: lexiProProduct.id,
        productName: lexiProProduct.name,
        description: lexiProProduct.description,
        priceId: lexiProPrice.id,
        interval: "month",
        priceType: "recurring",
        amount: lexiProPrice.unitAmount ?? lexiProPrice.unit_amount ?? 0,
        currency: lexiProPrice.currency || "aud",
      }
    : null;

  // Filter to get 7-Day Pass product (one-time payment)
  const dayPassProduct = stripePricesData?.products?.find(
    (product) =>
      (product?.name || "").toLowerCase().includes("7") &&
      (product?.name || "").toLowerCase().includes("day"),
  );

  const dayPassPrice = dayPassProduct?.prices?.find(
    (price) => price.type === "one_time" || !price.recurring,
  );

  const dayPassPlan = dayPassPrice
    ? {
        productId: dayPassProduct.id,
        productName: dayPassProduct.name,
        description: dayPassProduct.description,
        priceId: dayPassPrice.id,
        interval: "one_time",
        priceType: "one_time",
        amount: dayPassPrice.unitAmount ?? dayPassPrice.unit_amount ?? 0,
        currency: dayPassPrice.currency || "aud",
      }
    : null;

  // Check for query parameter and store in sessionStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const planParam = searchParams.get("plan");

    if (planParam === "pro" || planParam === "day-pass") {
      logger.log(
        `Detected plan=${planParam} query parameter, storing in sessionStorage`,
      );
      sessionStorage.setItem("selectedPlan", planParam);
    }
  }, [location.search]);

  useEffect(() => {
    // Check if user actually needs onboarding
    const needsOnboarding = getOnboardingStatus();

    if (!needsOnboarding) {
      // User doesn't need onboarding, redirect to today's tips
      logger.log("User doesn't need onboarding, redirecting to today's tips");
      let cancelled = false;
      landingAfterSignIn("/todays-tips").then((target) => {
        if (!cancelled) navigate(target, { replace: true });
      });
      return () => {
        cancelled = true;
      };
    }

    // Check if user is coming with pre-selected plan
    const preSelectedPlan = sessionStorage.getItem("selectedPlan");
    if (preSelectedPlan === "pro" || preSelectedPlan === "day-pass") {
      setIsAutoSelectingPro(true);
      setIsLoading(false);

      // Start rotating loading messages
      const messages = [
        "Getting things ready...",
        "Securing your checkout...",
        "Loading payment details...",
        "Almost ready...",
      ];
      let index = 0;
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingMessage(messages[index]);
      }, 1500);

      // Clean up interval
      return () => clearInterval(interval);
    }

    setIsLoading(false);
  }, [navigate]);

  // Separate effect to handle auto-selecting plan after prices load
  useEffect(() => {
    const preSelectedPlan = sessionStorage.getItem("selectedPlan");

    // Handle Pro plan auto-selection
    if (preSelectedPlan === "pro" && !isPriceLoading && lexiProPlan) {
      logger.log("Auto-selecting Pro plan from query param");
      setSelectedPlan("pro");
      setShowCheckout(true);
      setIsAutoSelectingPro(false);
      sessionStorage.removeItem("selectedPlan");
    }
    // Handle 7-Day Pass auto-selection
    else if (preSelectedPlan === "day-pass" && !isPriceLoading && dayPassPlan) {
      logger.log("Auto-selecting 7-Day Pass from query param");
      setSelectedPlan("day-pass");
      setShowCheckout(true);
      setIsAutoSelectingPro(false);
      sessionStorage.removeItem("selectedPlan");
    }
  }, [isPriceLoading, lexiProPlan, dayPassPlan]);

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan);

    if (plan === "free") {
      try {
        // Update onboarding status in backend if we have customerId
        if (customerId) {
          logger.log("Updating onboarding status for customer:", customerId);
          await updateOnboardingStatus(customerId, false);
        } else {
          logger.warn("No customerId available, skipping backend update");
        }

        // Send welcome email to the user
        const userEmail =
          sessionStorage.getItem("userEmail") ||
          localStorage.getItem("userEmail") ||
          "";
        if (userEmail) {
          sendWelcomeEmail({
            email: userEmail.toLowerCase(),
          }).catch((error) => {
            logger.error("Failed to send welcome email:", error);
            // Don't block the flow if email fails
          });
        }

        // Mark onboarding as complete in local storage
        sessionStorage.setItem("onboarding", "false");
        localStorage.setItem("onboarding", "false");
        trackCompleteRegistration();
        // Redirect to today's tips (or /home until today's tips are out)
        navigate(await landingAfterSignIn("/todays-tips"), { replace: true });
      } catch (error) {
        logger.error("Error updating onboarding status:", error);
        // Still redirect even if API call fails
        sessionStorage.setItem("onboarding", "false");
        localStorage.setItem("onboarding", "false");
        navigate(await landingAfterSignIn("/todays-tips"), { replace: true });
      }
    } else {
      // Show checkout for Pro plan or 7-Day Pass
      setShowCheckout(true);
    }
  };

  const closeCheckout = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  const refreshJWTAfterPayment = async (requirePaid = false) => {
    try {
      const currentToken =
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("authToken");
      if (!currentToken) {
        logger.warn("Cannot refresh JWT: no token found");
        return false;
      }

      logger.log("Refreshing JWT token after payment...");

      const response = await fetch("/api/refresh-jwt-after-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: currentToken,
        }),
      });

      if (!response.ok) {
        logger.warn("Failed to refresh JWT");
        return false;
      }

      const data = await response.json();
      if (data.token) {
        const payload = decodeJwtPayload(data.token);
        const hasSubscription = payload?.subscription?.some(sub => ['active', 'trialing'].includes(sub.status));
        const hasPass = payload?.oneTimePayments?.some(payment => payment.status === 'succeeded');
        if (requirePaid && !(selectedPlan === 'day-pass' ? hasPass : hasSubscription)) return false;
        const sid = sessionStorage.getItem('verificationSid') || localStorage.getItem('verificationSid');
        if (!sid) return false;
        storeAuthData(sid, userEmail, userPhone, data.token, 24);

        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("authTokenUpdated"));

        // Extract and persist product name from new token
        const subscription = payload?.subscription;
        const oneTimePayments = payload?.oneTimePayments;

        if (
          subscription &&
          Array.isArray(subscription) &&
          subscription.length > 0
        ) {
          const activeSubscription =
            subscription.find(
              (sub) => sub.status === "active" || sub.status === "trialing",
            ) || subscription[0];
          const productName =
            activeSubscription?.items?.[0]?.price?.product?.name;
          if (productName) {
            persistProductName(productName);
            logger.log("Product name updated to:", productName);
          }
        } else if (
          oneTimePayments &&
          Array.isArray(oneTimePayments) &&
          oneTimePayments.length > 0
        ) {
          const mostRecent = oneTimePayments.sort(
            (a, b) => b.created - a.created,
          )[0];
          const productName = mostRecent?.productName;
          if (productName) {
            persistProductName(productName);
            logger.log("Product name updated to:", productName);
          }
        }

        logger.log("JWT token refreshed successfully");
        return true;
      }
      return false;
    } catch (error) {
      logger.error("Error refreshing JWT after payment:", error);
      return false;
    }
  };

  const handleCheckoutSuccess = async (paymentIntent) => {
    logger.log("Payment successful, processing...");

    const metaPurchaseData = getMetaPurchaseData(
      paymentIntent,
      selectedPlan,
      lexiProPlan,
      dayPassPlan,
    );
    if (metaPurchaseData) {
      trackMetaPurchase(metaPurchaseData);
    }

    await completePaymentHandoff();
  };

  // Also used when the checkout finds the plan already paid for (a reload or
  // second tab after paying), which must not report another Purchase
  const completePaymentHandoff = async () => {
    setHandoffBusy(true);
    setHandoffError("");
    try {
      await waitForPaidSession(refreshJWTAfterPayment);
      if (!customerId) throw new Error('Unable to finish your session. Please retry.');
      await updateOnboardingStatus(customerId, false);
      if (!await refreshJWTAfterPayment(true)) throw new Error('Unable to refresh your session. Please retry.');
      sessionStorage.setItem('onboarding', 'false');
      localStorage.setItem('onboarding', 'false');
      window.dispatchEvent(new CustomEvent('lexi:product-updated', { detail: { productName: getStoredProductName() } }));
      navigate(await landingAfterSignIn('/todays-tips'), { replace: true });
    } catch (error) {
      setHandoffError(error.message);
    } finally {
      setHandoffBusy(false);
    }
  };

  const handleCheckoutError = (message) => {
    logger.error("Checkout error:", message);
  };

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login", { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-8">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-brand-pink" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      {/* Header with Logout */}
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200">
        <div className="flex-1" />
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl">
          {handoffBusy && <p role="status" className="mb-4 text-center">Finishing your login and updating access...</p>}
          {handoffError && <div role="alert" className="mb-4 rounded-lg bg-red-50 p-4">
            <p>{handoffError}</p>
            <Button onClick={completePaymentHandoff} disabled={handoffBusy}>Retry entering the app</Button>
          </div>}
          {showCheckout ? (
            /* Checkout View */
            <OnboardingCheckout
              lexiProPlan={
                selectedPlan === "day-pass" ? dayPassPlan : lexiProPlan
              }
              userEmail={userEmail}
              userPhone={userPhone}
              stripeCustomerId={stripeCustomerId}
              customerId={customerId}
              onSuccess={handleCheckoutSuccess}
              onAlreadyPurchased={completePaymentHandoff}
              onError={handleCheckoutError}
              onClose={closeCheckout}
              refreshJWT={refreshJWTAfterPayment}
            />
          ) : isAutoSelectingPro ? (
            /* Loading Skeleton - Mimics Stripe Checkout */
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
              {/* Loading message */}
              <div className="text-center mb-8">
                <Loader2 className="w-12 h-12 mx-auto text-pink-600 animate-spin mb-4" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  {loadingMessage}
                </h2>
                <p className="text-slate-600 text-sm">
                  Please wait while we prepare everything for you
                </p>
              </div>

              <div className="animate-pulse space-y-6">
                {/* Header skeleton */}
                <div className="space-y-3">
                  <div className="h-8 bg-slate-200 rounded w-3/4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </div>

                {/* Price display skeleton */}
                <div className="border-t border-b border-slate-200 py-6 space-y-3">
                  <div className="flex justify-between">
                    <div className="h-5 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-5 bg-slate-200 rounded w-1/4"></div>
                  </div>
                  <div className="flex justify-between">
                    <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/5"></div>
                  </div>
                </div>

                {/* Form fields skeleton */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                    <div className="h-12 bg-slate-200 rounded"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-12 bg-slate-200 rounded"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                    <div className="h-12 bg-slate-200 rounded"></div>
                  </div>
                </div>

                {/* Button skeleton */}
                <div className="h-12 bg-slate-200 rounded"></div>

                {/* Footer text skeleton */}
                <div className="space-y-2">
                  <div className="h-3 bg-slate-200 rounded w-full"></div>
                  <div className="h-3 bg-slate-200 rounded w-5/6"></div>
                </div>
              </div>
            </div>
          ) : (
            /* Plan Selection View */
            <div className="space-y-8">
              <div className="text-center">
                <h1 className="text-3xl font-bold text-slate-900 mb-3">
                  Choose Your Plan
                </h1>
                <p className="text-slate-600 text-lg">
                  You&apos;re all set! Now choose how you&apos;d like to continue.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Free Plan */}
                <div
                  onClick={() => handlePlanSelect("free")}
                  className="cursor-pointer p-8 border-2 border-slate-200 hover:border-pink-400 rounded-2xl transition-all hover:shadow-2xl bg-white"
                >
                  <div className="flex flex-col h-full">
                    <div className="mb-6">
                      <h3 className="font-bold text-2xl text-slate-900">
                        Lexi Social
                      </h3>
                      <p className="text-pink-600 font-semibold text-sm mt-2">
                        No card needed
                      </p>
                    </div>

                    <div className="mb-8">
                      <span className="text-4xl font-bold text-slate-900">
                        FREE
                      </span>
                    </div>

                    <ul className="space-y-4 mb-8 flex-grow">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          2 morning tips
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          All races
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          Race results
                        </span>
                      </li>
                    </ul>

                    <Button
                      className="w-full bg-slate-100 text-slate-900 hover:bg-slate-200 border-2 border-slate-300 h-13 py-3 font-semibold text-base"
                      disabled={selectedPlan === "free"}
                    >
                      {selectedPlan === "free" ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        "Get Started Free"
                      )}
                    </Button>
                  </div>
                </div>

                {/* Pro Plan */}
                <div
                  onClick={() => handlePlanSelect("pro")}
                  className="cursor-pointer p-8 border-2 border-pink-400 rounded-2xl transition-all hover:shadow-2xl bg-gradient-to-br from-pink-50 to-white relative"
                >
                  <div className="absolute top-6 right-6 bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-bold">
                    Most Popular
                  </div>

                  <div className="flex flex-col h-full">
                    <div className="mb-6 pr-24">
                      <h3 className="font-bold text-2xl text-slate-900">
                        Lexi Pro
                      </h3>
                      <p className="text-pink-600 font-semibold text-sm mt-2">
                        Best value
                      </p>
                    </div>

                    <div className="mb-8">
                      <span className="text-4xl font-bold text-slate-900">
                        {lexiProPlan
                          ? formatPrice(
                              lexiProPlan.amount,
                              lexiProPlan.currency,
                            )
                          : "$29.95"}
                      </span>
                      <span className="text-base text-slate-600 ml-3">
                        /month
                      </span>
                    </div>

                    <ul className="space-y-4 mb-8 flex-grow">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          4 daily tips
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          Lexi Chat
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          History access
                        </span>
                      </li>
                    </ul>

                    <Button
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white h-13 py-3 font-semibold text-base"
                      disabled={selectedPlan === "pro"}
                    >
                      {selectedPlan === "pro" ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Redirecting...
                        </>
                      ) : (
                        "Upgrade Now"
                      )}
                    </Button>
                  </div>
                </div>

                {/* 7-Day Pass */}
                <div
                  onClick={() => handlePlanSelect("day-pass")}
                  className="cursor-pointer p-8 border-2 border-blue-400 rounded-2xl transition-all hover:shadow-2xl bg-gradient-to-br from-blue-50 to-white relative"
                >
                  <div className="flex flex-col h-full">
                    <div className="mb-6">
                      <h3 className="font-bold text-2xl text-slate-900">
                        7-Day Pass
                      </h3>
                      <p className="text-blue-600 font-semibold text-sm mt-2">
                        Try it out
                      </p>
                    </div>

                    <div className="mb-8">
                      <span className="text-4xl font-bold text-slate-900">
                        {dayPassPlan
                          ? formatPrice(
                              dayPassPlan.amount,
                              dayPassPlan.currency,
                            )
                          : "$9.95"}
                      </span>
                      <span className="text-base text-slate-600 ml-3">
                        once
                      </span>
                    </div>

                    <ul className="space-y-4 mb-8 flex-grow">
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          4 daily tips
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          Lexi Chat
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 font-medium">
                          7 days access
                        </span>
                      </li>
                    </ul>

                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-13 py-3 font-semibold text-base"
                      disabled={selectedPlan === "day-pass"}
                    >
                      {selectedPlan === "day-pass" ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Redirecting...
                        </>
                      ) : (
                        "Buy Now"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
